import Vue from 'vue'
import VueRouter from 'vue-router'
import Layout from '@/layout'
import { getToken, getUserInfo } from '@/utils/auth'
import contractsRoutes from './contracts' // 导入合同路由模块
import basicRoutes from './basic' // 导入基本信息路由模块
import ordersRoutes from './modules/orders' // 导入订单路由模块
import settlementRoutes from './modules/settlement' // 导入结算路由模块
import financeRoutes from './modules/finance' // 导入资金路由模块
import dataRoutes from './modules/data' // 导入数据管理路由模块
import systemRoutes from './modules/system' // 导入系统管理路由模块

Vue.use(VueRouter)

// 静态路由配置
const constantRoutes = [
  {
    path: '/',
    component: Layout,
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index'),
        meta: { title: '首页', icon: 'dashboard', requiresAuth: true }
      },
      {
        path: 'api-test',
        name: 'ApiTest',
        component: () => import('@/views/api-test'),
        meta: { title: 'API测试', icon: 'el-icon-s-platform', requiresAuth: false }
      }
    ]
  },
  // 添加合同路由
  contractsRoutes,
  // 添加基本信息路由
  basicRoutes,
  // 添加订单路由
  ordersRoutes,
  // 添加结算路由
  settlementRoutes,
  // 添加资金路由
  financeRoutes,
  // 添加数据管理路由
  dataRoutes,
  // 添加系统管理路由
  systemRoutes,
  {
    path: '/profile',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '',
        name: 'Profile',
        component: () => import('@/views/profile/index'),
        meta: { title: '个人信息', requiresAuth: true }
      }
    ]
  },
  {
    path: '/change-password',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '',
        name: 'ChangePassword',
        component: () => import('@/views/profile/change-password'),
        meta: { title: '修改密码', requiresAuth: true }
      }
    ]
  },
  {
    path: '/login',
    component: () => import('@/views/login/index'),
    meta: { requiresAuth: false },
    hidden: true
  },
  {
    path: '/404',
    component: () => import('@/views/error-page/404'),
    meta: { requiresAuth: false },
    hidden: true
  }
]

// 初始化路由
const router = new VueRouter({
  routes: constantRoutes
})

// 处理动态路由
export function initDynamicRoutes() {
  const userInfo = getUserInfo()
  
  // 优先尝试从localStorage获取权限树
  let permissionTree = []
  try {
    const treeStr = localStorage.getItem('permissionTree')
    if (treeStr) {
      permissionTree = JSON.parse(treeStr)
    }
  } catch (error) {
    console.error('解析权限树失败:', error)
  }
  
  // 转换权限树为菜单树
  let menuTree = []
  if (permissionTree && permissionTree.length > 0) {
    console.log('使用权限树初始化动态路由')
    menuTree = convertPermissionTreeToMenuTree(permissionTree)
  } else if (userInfo && userInfo.menus && userInfo.menus.length > 0) {
    // 后备：使用旧版menus
    console.log('权限树为空，使用旧版menus初始化动态路由')
    menuTree = buildMenuTree(userInfo.menus)
  } else {
    console.log('用户信息或菜单数据不存在，跳过动态路由初始化')
    return
  }

  console.log('开始初始化动态路由，菜单数据:', menuTree)

  // 动态添加路由
  const dynamicRoutes = []
  
  // 通配符路由应该最后添加
  const notFoundRoute = { path: '*', redirect: '/404', hidden: true }
  
  // 遍历菜单树，生成动态路由
  menuTree.forEach(menu => {
    // 如果有子菜单
    if (menu.children && menu.children.length > 0) {
      const parentRoute = {
        path: menu.path,
        component: Layout,
        redirect: menu.children[0].path,
        name: menu.name,
        meta: { title: menu.name, icon: getMenuIcon(menu.name), requiresAuth: true },
        children: []
      }
      
      // 处理子路由
      menu.children.forEach(child => {
        // 从path路径提取组件路径
        const componentPath = getComponentPath(child.path)
        console.log(`处理子路由: ${child.path} -> 组件路径: ${componentPath}`)
        parentRoute.children.push({
          path: child.path.replace(menu.path + '/', ''), // 子路径相对于父路径
          name: child.name,
          component: () => import(`@/views${componentPath}.vue`),
          meta: { title: child.name, requiresAuth: true }
        })
      })
      
      dynamicRoutes.push(parentRoute)
    } else {
      // 没有子菜单的叶子节点，直接作为独立路由
      if (menu.path && menu.path !== '/') {
        const componentPath = getComponentPath(menu.path)
        console.log(`处理独立路由: ${menu.path} -> 组件路径: ${componentPath}`)
        const singleRoute = {
          path: menu.path,
          component: Layout,
          children: [
            {
              path: 'index',
              name: menu.name,
              component: () => import(`@/views${componentPath}.vue`),
              meta: { title: menu.name, icon: getMenuIcon(menu.name), requiresAuth: true }
            }
          ]
        }
        dynamicRoutes.push(singleRoute)
      }
    }
  })
  
  console.log('生成的动态路由:', dynamicRoutes)
  
  // 添加动态路由
  dynamicRoutes.forEach(route => {
    router.addRoute(route)
  })
  
  // 添加通配符路由
  router.addRoute(notFoundRoute)
  
  console.log('动态路由初始化完成')
}

// 将权限树转换为菜单树
function convertPermissionTreeToMenuTree(permissionTree) {
  function convertPermissionToMenu(permission) {
    // 递归处理子菜单，只包含showInMenu为true的菜单
    const children = permission.children && permission.children.length > 0
      ? permission.children
          .filter(child => {
            // 子菜单必须是MENU类型，且showInMenu为true（如果字段存在）
            return child.permissionType === 'MENU' && 
                   (child.showInMenu === true || child.showInMenu === undefined)
          })
          .map(convertPermissionToMenu)
      : []
    
    return {
      id: permission.id,
      name: permission.permissionName,
      path: permission.resourcePath,
      icon: permission.description, // 图标信息在description中
      parentId: permission.parentId,
      sort: permission.sortOrder,
      permissionCode: permission.permissionCode,
      showInMenu: permission.showInMenu,
      children: children.length > 0 ? children : undefined
    }
  }
  
  // 只包含showInMenu为true的顶层菜单
  // 如果showInMenu字段不存在，默认为true（向后兼容）
  return permissionTree
    .filter(perm => {
      return perm.permissionType === 'MENU' && 
             (perm.showInMenu === true || perm.showInMenu === undefined)
    })
    .map(convertPermissionToMenu)
    .filter(menu => {
      // 如果菜单没有子菜单，但showInMenu为false，则不显示
      // 如果有子菜单，即使自身showInMenu为false，如果有子菜单要显示，也应该显示父菜单
      if (menu.children && menu.children.length > 0) {
        return true // 有子菜单就显示父菜单
      }
      return menu.showInMenu === true || menu.showInMenu === undefined
    })
}

// 构建菜单树结构（用于旧版menus）
function buildMenuTree(menus) {
  const menuMap = new Map()
  const rootMenus = []
  
  // 首先创建所有菜单项的映射，并保证children为数组
  menus.forEach(menu => {
    menuMap.set(menu.id, {
      ...menu,
      children: Array.isArray(menu.children) ? menu.children : [],
    })
  })
  
  // 构建树结构
  menus.forEach(menu => {
    const menuItem = menuMap.get(menu.id)
    if (menu.parentId === 0) {
      rootMenus.push(menuItem)
    } else {
      const parentMenu = menuMap.get(menu.parentId)
      if (parentMenu) {
        parentMenu.children.push(menuItem)
      }
    }
  })
  
  return rootMenus
}

// 帮助函数：获取菜单图标
function getMenuIcon(label) {
  const iconMap = {
    '基本信息': 'el-icon-document',
    '客户管理': 'el-icon-user',
    '合同管理': 'el-icon-tickets',
    '订单管理': 'el-icon-shopping-cart-full',
    '结算管理': 'el-icon-trophy',
    '资金管理': 'el-icon-money',
    '数据管理': 'el-icon-s-data'
  }
  return iconMap[label] || 'el-icon-s-operation'
}

// 帮助函数：从路由路径获取组件路径
// 当权限配置的 resourcePath 与组件文件名不一致时，在此添加映射
const pathToComponentMap = {
  '/basic/products': '/basic/product-management'  // 产品管理：权限路径 /basic/products -> 组件 product-management.vue
}
function getComponentPath(route) {
  const mapped = pathToComponentMap[route]
  if (mapped) return mapped
  return route.replace(/\/index$/, '')
}

// 导航守卫
router.beforeEach((to, from, next) => {
  // 获取token
  const hasToken = getToken()
  console.log('路由导航:', to.path, '是否有token:', !!hasToken)
  
  // 判断页面是否需要登录权限
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  
  if (requiresAuth) {
    // 页面需要登录权限
    if (hasToken) {
      // 已登录，允许访问
      console.log('已授权访问:', to.path)
      next()
    } else {
      // 未登录，重定向到登录页面
      console.log('未授权，重定向到登录页面')
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }
  } else {
    // 页面不需要登录权限，直接访问
    if (to.path === '/login' && hasToken) {
      // 已登录状态下访问登录页面，重定向到首页
      console.log('已登录状态访问登录页面，重定向到首页')
      next({ path: '/dashboard' })
    } else {
      console.log('直接访问无需授权的页面:', to.path)
      next()
    }
  }
})

export default router 
